import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Avatar,
  IconButton,
  Button,
  CircularProgress,
} from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import ReplayIcon from "@mui/icons-material/Replay";
import AddIcon from "@mui/icons-material/Add";
import { useTheme } from "@mui/material/styles";

import { getStyles } from "../index";
import { availableSpeakers } from "./functions";
import { db } from "./firebase";
import { doc, getDoc } from "firebase/firestore";

const FeaturedPodcasts2 = ({ user }) => {
  const featuredPodcastIds = [
    "13pRfBAUwv9detgfoNIg",
    "BwDTyk9bbuja3mrtQzVi",
    "GGa7P52ZqeFHljiBaA6R",
  ];

  const [podcasts, setPodcasts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [playingPodcast, setPlayingPodcast] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef = useRef(null);

  const navigate = useNavigate();
  const theme = useTheme();
  const styles = getStyles(theme);

  useEffect(() => {
    const fetchPodcasts = async () => {
      try {
        setLoading(true);
        const podcastsData = [];

        for (const podcastId of featuredPodcastIds) {
          const docRef = doc(db, "podcasts", podcastId);
          const docSnap = await getDoc(docRef);

          if (docSnap.exists()) {
            podcastsData.push({
              id: docSnap.id,
              ...docSnap.data(),
            });
          }
        }

        setPodcasts(podcastsData);
      } catch (error) {
        console.error("Error fetching featured podcasts:", error);
        setError("Failed to load featured podcasts");
      } finally {
        setLoading(false);
      }
    };

    fetchPodcasts();
  }, []);

  useEffect(() => {
    if (audioRef.current) {
      const audio = audioRef.current;

      const updateProgress = () => {
        if (audio) {
          setCurrentTime(audio.currentTime);
        }
      };

      const handleLoadedMetadata = () => {
        setDuration(audio.duration);
      };

      const handleEnded = () => {
        setIsPlaying(false);
        setCurrentTime(0);
        if (audio) {
          audio.currentTime = 0;
        }
      };

      audio.addEventListener("timeupdate", updateProgress);
      audio.addEventListener("loadedmetadata", handleLoadedMetadata);
      audio.addEventListener("ended", handleEnded);

      return () => {
        audio.removeEventListener("timeupdate", updateProgress);
        audio.removeEventListener("loadedmetadata", handleLoadedMetadata);
        audio.removeEventListener("ended", handleEnded);
      };
    }
  }, [playingPodcast]);

  const togglePlayPause = (e, podcast) => {
    e.stopPropagation();
    if (!audioRef.current) return;

    if (playingPodcast && playingPodcast.id !== podcast.id) {
      setPlayingPodcast(podcast);
      setIsPlaying(true);
      audioRef.current.src = podcast.podcastUrl;
      audioRef.current.play();
    } else if (playingPodcast && playingPodcast.id === podcast.id) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    } else {
      setPlayingPodcast(podcast);
      setIsPlaying(true);
      audioRef.current.src = podcast.podcastUrl;
      audioRef.current.play();
    }
  };

  const handleSeek = (e, podcast) => {
    e.stopPropagation();
    if (
      audioRef.current &&
      playingPodcast &&
      playingPodcast.id === podcast.id
    ) {
      const seekTime =
        (e.nativeEvent.offsetX / e.target.clientWidth) * duration;
      audioRef.current.currentTime = seekTime;
      setCurrentTime(seekTime);
    }
  };

  const handleRestart = (e, podcast) => {
    e.stopPropagation();
    if (
      audioRef.current &&
      playingPodcast &&
      playingPodcast.id === podcast.id
    ) {
      audioRef.current.currentTime = 0;
      setCurrentTime(0);
      if (!isPlaying) {
        audioRef.current.play();
        setIsPlaying(true);
      }
    }
  };

  const formatTime = (time) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds < 10 ? "0" : ""}${seconds}`;
  };

  const getSpeakerAvatar = (speakerId) => {
    const speaker = availableSpeakers.find(
      (speaker) => speaker.id === speakerId,
    );
    return speaker ? speaker.avatar : null;
  };

  const handlePodcastClick = (podcastId) => {
    navigate(`/app/podcast/${podcastId}`);
  };

  const handleCreatePodcast = () => {
    navigate("/app/podcast");
  };

  const truncateName = (name, length = 25) => {
    if (!name) return "Untitled Podcast";
    return name.length > length ? name.substring(0, length) + "..." : name;
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Typography variant="body1" color="error" sx={{ p: 2 }}>
        {error}
      </Typography>
    );
  }

  return (
    <Paper
      sx={{
        background: "linear-gradient(to right, #3b82f6, #4f46e5)",
        padding: 5,
        color: "white",
        boxShadow: "none",
        borderRadius: 4,
        overflow: "hidden",
        mb: 4,
        mt: 10,
      }}
    >
      <Typography
        variant="h5"
        sx={{ mb: 1, fontWeight: 700, fontSize: "2rem" }}
      >
        Populära podcasts
      </Typography>
      <Typography
        variant="body1"
        sx={{
          mb: 3,
        }}
      >
        Utvalda podcasts genererade av våra användare
      </Typography>
      <audio ref={audioRef} style={{ display: "none" }} />

      {/* Featured Podcasts List */}
      <Box sx={{ mb: 3 }}>
        {podcasts.map((podcast) => (
          <Box
            key={podcast.id}
            sx={{
              p: 2,
              mb: 2,
              borderRadius: 2,
              bgcolor: "rgba(255, 255, 255, 0.1)",
              cursor: "pointer",
              transition: "all 0.2s ease",
              "&:hover": {
                bgcolor: "rgba(255, 255, 255, 0.15)",
              },
            }}
            onClick={() => handlePodcastClick(podcast.id)}
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              {/* Avatar Section */}
              <Box sx={{ display: "flex", mr: 2 }}>
                <Avatar
                  src={getSpeakerAvatar(podcast.speaker1)}
                  alt="Speaker 1"
                  sx={{
                    width: 32,
                    height: 32,
                    mr: -0.5,
                    border: "1px solid white",
                  }}
                />
                <Avatar
                  src={getSpeakerAvatar(podcast.speaker2)}
                  alt="Speaker 2"
                  sx={{ width: 32, height: 32, border: "1px solid white" }}
                />
              </Box>

              {/* Title */}
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: "bold",
                  flexGrow: 1,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  maxWidth: { xs: "100px", sm: "200px", md: "300px" },
                }}
              >
                {truncateName(podcast.podcastName)}
              </Typography>

              {/* Player Controls */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  ml: 2,
                  flexGrow: 1,
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <IconButton
                  size="small"
                  onClick={(e) => togglePlayPause(e, podcast)}
                  sx={{
                    bgcolor: "white",
                    color: "#4f46e5",
                    width: 36,
                    height: 36,
                    mr: 2,
                    "&:hover": { bgcolor: "rgba(255, 255, 255, 0.9)" },
                  }}
                >
                  {isPlaying && playingPodcast?.id === podcast.id ? (
                    <PauseIcon fontSize="small" />
                  ) : (
                    <PlayArrowIcon fontSize="small" />
                  )}
                </IconButton>

                <Box
                  sx={{
                    flexGrow: 1,
                    maxWidth: { xs: "120px", sm: "200px", md: "300px" },
                  }}
                >
                  <Box
                    sx={{
                      width: "100%",
                      height: 6,
                      backgroundColor: "rgba(255, 255, 255, 0.2)",
                      borderRadius: 3,
                      position: "relative",
                      cursor: "pointer",
                    }}
                    onClick={(e) => handleSeek(e, podcast)}
                  >
                    <Box
                      sx={{
                        position: "absolute",
                        height: "100%",
                        width:
                          playingPodcast?.id === podcast.id
                            ? `${(currentTime / duration) * 100}%`
                            : "0%",
                        backgroundColor: "white",
                        borderRadius: 3,
                      }}
                    />
                  </Box>
                </Box>

                <Typography
                  variant="caption"
                  sx={{ ml: 2, minWidth: 45, textAlign: "right" }}
                >
                  {playingPodcast?.id === podcast.id
                    ? formatTime(currentTime)
                    : "--:--"}
                </Typography>

                <IconButton
                  size="small"
                  onClick={(e) => handleRestart(e, podcast)}
                  sx={{
                    ml: 1,
                    color: "white",
                    "&:hover": { bgcolor: "rgba(255, 255, 255, 0.1)" },
                  }}
                >
                  <ReplayIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>
          </Box>
        ))}
      </Box>

      {/* Call to Action - Generate Podcast */}
      <Box sx={{ textAlign: "center" }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreatePodcast}
          sx={{
            bgcolor: "white",
            color: "#4f46e5",
            fontWeight: 600,
            px: 3,
            py: 1,
            "&:hover": {
              bgcolor: "rgba(255, 255, 255, 0.9)",
            },
          }}
        >
          Generera podcast
        </Button>
      </Box>
    </Paper>
  );
};

export default FeaturedPodcasts2;
